import { useAuthStore } from '@/stores'
import { isStaleIdentityError } from './identityEpoch'
import { isTransientAuthError } from './transientAuthError'

/**
 * Shared 401-recovery controller used by BOTH the axios client
 * (services/api.ts) and the fetch client (services/api/client.ts) so a
 * lapsed access token is recovered identically no matter which client a
 * given request went through. Extracted from services/api.ts (which
 * originally closed over this logic per-`createApiClient()` call) rather
 * than duplicated, per the fix requirement that both clients share ONE
 * implementation.
 *
 * `createAuthRecovery()` returns a fresh, isolated instance (its own closure
 * state) — call it once per client instance so concurrent 401s from that
 * client single-flight through exactly ONE re-mint, and so tests get
 * isolated state the same way the pre-extraction per-`createApiClient()`
 * closures did.
 */
export interface AuthRecoveryController {
  /**
   * Re-mint the current-scope access token from the still-valid session:
   *   - an in-memory refresh token when present, else
   *   - the HttpOnly session cookie via SSO silent refresh, else
   *   - the HttpOnly session cookie via switch-tenant re-issue.
   * Concurrent callers share ONE in-flight re-mint (single-flight via the
   * closed-over promise below — not a numeric retry count, so there is no
   * "max attempts" constant to name: at most one re-mint is ever in flight,
   * and every concurrent 401 awaits that same promise instead of starting
   * its own). Resolves true only when a fresh token was stored; never throws.
   */
  remintAccessToken: () => Promise<boolean>
  /**
   * Ambiguous 401 (not a recoverable re-mint, or a re-mint attempt that
   * failed): probe the canonical session-liveness endpoint via the
   * caller-supplied `probe`, and only force a logout+redirect if the probe
   * ITSELF definitively 401s. Any other probe failure (network error, 5xx,
   * timeout) is NOT a signal that the token is actually invalid, so the
   * session is left intact.
   *
   * `probe` must reject with a `{status}` or `{response:{status}}`-shaped
   * error on failure (matches both the axios and fetch error shapes), and
   * should hit `/auth/me` — the caller passes the concrete request so this
   * module stays free of any client-specific request shape or literal URL.
   *
   * Guarded so concurrent callers don't spawn multiple probe+redirect
   * cycles.
   */
  handleAmbiguous401: (probe: () => Promise<unknown>) => void
}

export function createAuthRecovery(): AuthRecoveryController {
  // Single-flight token re-mint. Concurrent 401s (e.g. several TanStack
  // Query refetches firing at once as a token lapses, from either client)
  // share ONE refresh instead of each stampeding the refresh endpoint and
  // racing to overwrite the freshly minted token.
  let reauthPromise: Promise<boolean> | null = null
  // Guards the ambiguous-401 probe+redirect cycle so concurrent background
  // refetch failures don't spawn multiple logout+redirect cycles.
  let redirecting = false

  const remintAccessToken = (): Promise<boolean> => {
    if (!reauthPromise) {
      reauthPromise = (async () => {
        const store = useAuthStore.getState()
        try {
          if (store.authMethod === 'sso' && !store.refreshToken) {
            // SSO: silently re-mint the session cookie via the silent-refresh
            // endpoint (store.ssoRefreshSession). Re-minting from switchTenant
            // here would use the already-expired SSO session cookie and
            // always fail.
            await store.ssoRefreshSession()
          } else {
            // Local auth: ALWAYS go through refreshSession, with or without an
            // in-memory refresh token — it sends an empty token and the server
            // falls back to the HttpOnly refresh cookie (WS-2). The refresh
            // token is never persisted, so after ANY full page load it is
            // null; the old branch then called switchTenant with the already-
            // expired bearer, which 401'd, and the probe signed the user out
            // 8 minutes after every reload (live finding 2026-09-06).
            try {
              await store.refreshSession()
            } catch (err) {
              // An aborted (identity-changed) refresh must not fall back to
              // switchTenant under the NEW identity (#301).
              if (isStaleIdentityError(err)) throw err
              // A throttled/unavailable refresh is not a missing cookie: do not
              // fall through to a second auth call (that would only add load).
              if (isTransientAuthError(err)) throw err
              // Last resort for platform-scope sessions whose refresh cookie is
              // absent: switch-tenant re-issues the same-scope bearer from the
              // session cookie. Rethrow if that fails too.
              if (store.authMethod === 'sso') throw err
              await store.switchTenant(store.user?.tenantId ?? '')
            }
          }
          return true
        } catch {
          return false
        }
      })()
      // Release the latch once settled so the NEXT lapse re-mints afresh;
      // in-flight awaiters already hold the resolved promise.
      void reauthPromise.finally(() => {
        reauthPromise = null
      })
    }
    return reauthPromise
  }

  const handleAmbiguous401 = (probe: () => Promise<unknown>): void => {
    if (redirecting) return
    redirecting = true
    probe()
      .then(() => {
        // Session still valid — the original 401 was a resource-authorization
        // failure, not expiry. Leave the session intact.
      })
      .catch((probeError: unknown) => {
        // Only a DEFINITIVE 401 from the probe means the session token itself
        // is rejected → real expiry, log out. Any other probe failure
        // (network error, 5xx, timeout) is NOT a signal about the token.
        const probeStatus =
          (probeError as { status?: number } | undefined)?.status ??
          (probeError as { response?: { status?: number } } | undefined)?.response?.status
        if (probeStatus === 401) {
          // Navigate only after the server revoke settles: navigating aborts it.
          void useAuthStore
            .getState()
            .logout()
            .then(() => {
              if (typeof window !== 'undefined') {
                window.location.href = '/login'
              }
            })
        }
      })
      .finally(() => {
        // Always release the guard so a later genuine expiry still redirects
        // (e.g. after a beforeunload "leave site?" prompt cancels a redirect).
        redirecting = false
      })
  }

  return { remintAccessToken, handleAmbiguous401 }
}
